import React, { useContext } from "react";
import { isMobile, isIOS } from "react-device-detect";
import styles from "./ShareArrow.module.scss";
import CopiedContext from "../CopiedContext";
import { clearPreview } from "../utils/storyUIHandlers";
import { trackEvent } from "../utils/helpers";

const ShareArrow = ({
  title,
  url,
  text,
  downloadUrl,
  isVideo = false,
  customBottom = "8px",
  sectionName,
  mediaShare,
  cityurl,
  venue,
  azureurl,
  videourl,
  guid,
  country,
}) => {
  const { showCopied, setShowCopied } = useContext(CopiedContext);

  // Strip any trailing single quotes from URLs returned by the API
  const cleanDownloadUrl = downloadUrl?.replace(/'+$/, "");

  // Build mediaShareURL like ShareBar does
  const mediaShareAddress = `https://${cityurl}/stories-shared-media`;
  let mediaShareURL = "";
  let shareText = "";

  const thumbnailParts = mediaShare?.thumbnail?.split("/");
  const filename = thumbnailParts?.[4];
  const thumbnailURL = filename?.split("_")[0];

  // Extract venue parameter for all URLs
  const venueParam = venue ? `&venue=${encodeURIComponent(venue)}` : "";

  // Use the text prop passed from the component, which already has the correct format
  shareText = text ? `${text} ` : "Check out this content from our Electric Shuffle visit. ";

  if (mediaShare?.mediaType === "PHOTO") {
    mediaShareURL = `${mediaShareAddress}?u=photo/${mediaShare.mediaURI.substring(0, mediaShare.mediaURI.lastIndexOf("."))}&t=i&section=group_photo${venueParam}`;
  } else if (mediaShare?.mediaType === "VIDEO" && sectionName === "Action Replay") {
    if (mediaShare?.videoURL) {
      // Extract asset code from Electric Shuffle blob storage URL
      // URL format: https://esstoriesvideostorage.blob.core.windows.net/asset-[guid]/[guid]_1920x1080.mp4
      const urlParts = mediaShare.videoURL.split("/");
      const assetContainer = urlParts[urlParts.length - 2]; // asset-[guid]
      const filename = urlParts[urlParts.length - 1]; // [guid]_1920x1080.mp4
      const assetPath = `${assetContainer}/${filename}`;

      const vidType = "vidblob";
      mediaShareURL = `${mediaShareAddress}?u=${vidType}/${assetPath}&t=v&p=a&utm_source=Stories&utm_medium=link_share&utm_campaign=action_replay`;
    }
  } else if (mediaShare?.mediaType === "VIDEO" && sectionName === "Loop Cams") {
    if (mediaShare?.videoURL) {
      // Extract asset code from Electric Shuffle blob storage URL
      const urlParts = mediaShare.videoURL.split("/");
      const assetContainer = urlParts[urlParts.length - 2]; // asset-[guid]
      const filename = urlParts[urlParts.length - 1]; // [guid]_1920x1080.mp4
      const assetPath = `${assetContainer}/${filename}`;

      const vidType = "vidblob";
      mediaShareURL = `${mediaShareAddress}?u=${vidType}/${assetPath}&t=v&p=a&utm_source=Stories&utm_medium=link_share&utm_campaign=loop_cam`;
    }
  }

  // For US, only override if we don't already have a specific media share URL
  if (country === "us" && !mediaShareURL) {
    mediaShareURL = `https://stories.us.electricshuffle.com/${guid}`;
  }

  const handleShare = async (event) => {
    // Prevent the click from bubbling up to parent elements
    event.stopPropagation();

    trackEvent("Share", "share_link", sectionName);
    trackEvent("Share", "Native Share " + sectionName, "Mobile Share Button");

    // Create the native share text with current story URL
    const currentUrl = window.location.href;
    const fullShareText = `${shareText}See the full Story here: ${currentUrl}?utm_source=Stories&utm_medium=native_share&utm_campaign=native`;

    // Check if native share is supported
    if (!navigator.share) {
      // console.log("Native share not supported");
      // Fallback to copy URL
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(fullShareText);
          // console.log("URL copied to clipboard");
          setShowCopied(true);
        } catch (error) {
          // console.log("Failed to copy URL:", error);
        }
      }
      return;
    }

    // iOS: Try to fetch and share the actual file (full functionality)
    if (isIOS && cleanDownloadUrl) {
      try {
        const response = await fetch(cleanDownloadUrl, { credentials: "omit" });
        const blob = await response.blob();
        const fileName = isVideo ? "electric-shuffle-video.mp4" : "electric-shuffle-photo.jpg";
        const mimeType = isVideo ? "video/mp4" : "image/jpeg";
        const file = new File([blob], fileName, { type: mimeType });

        // Check if we can share files
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: title || "Electric Shuffle Stories",
            text: fullShareText,
            files: [file],
          });
          return;
        }
      } catch (error) {
        if (error.name === "AbortError" || error.name === "NotAllowedError") return;
      }
    }

    // Fallback: Share URL and text (works on both iOS and Android)
    try {
      const shareData = {
        title: title || "Electric Shuffle Stories",
        text: fullShareText,
      };

      // Add URL separately for iOS (better support)
      if (isIOS) {
        shareData.url = currentUrl;
      }

      await navigator.share(shareData);
    } catch (error) {
      if (error.name === "AbortError") return; // User dismissed — don't copy to clipboard
      // console.log("Error sharing:", error);
      // Final fallback to copy URL if share fails
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(fullShareText);
          // console.log("Share failed, URL copied to clipboard instead");
          setShowCopied(true);
        } catch (clipboardError) {
          // console.log("Failed to copy URL:", clipboardError);
        }
      }
    }
  };

  return (
    <div
      className={styles.shareContainer}
      onMouseEnter={(e) => {
        e.stopPropagation();
        clearPreview();
      }}
      onMouseOver={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
    >
      {/* Download Button - desktop only */}
      {!isMobile && (
        <a
          href={!isVideo ? undefined : cleanDownloadUrl}
          download={isVideo ? true : undefined}
          className={`${styles.shareIcon} ${sectionName === "Group Photo" || sectionName === "Loop Cams" ? styles.groupPhotoShare : ""}`}
          onClick={async (e) => {
            e.stopPropagation();
            trackEvent("Share", "Download " + sectionName, "Download Button");
            if (!isVideo) {
              e.preventDefault();
              try {
                const response = await fetch(cleanDownloadUrl, { credentials: "omit" });
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = objectUrl;
                link.download = "electric-shuffle-photo.jpg";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(objectUrl);
              } catch (error) {}
            }
          }}
          aria-label="Download"
        >
          <img src="/img/download-icon.svg" className={styles.downloadIcon} alt="Download" />
        </a>
      )}

      {/* Share Button - Only show on mobile */}
      {isMobile && (
        <button
          className={`${styles.shareIcon} ${sectionName === "Group Photo" || sectionName === "Loop Cams" ? styles.groupPhotoShare : ""}`}
          onClick={handleShare}
          aria-label="Share"
        >
          <img src="/img/share-icon.svg" alt="Share" />
        </button>
      )}
    </div>
  );
};

export default ShareArrow;
